/**
 * ParentIdvCard
 *
 * Pure component for displaying parent IDV (Indefinite Delivery Vehicle) information.
 * Shows vehicle type, award indicator, and links to parent award.
 */
import * as React from 'react'

import { ExternalLink, FileText } from 'lucide-react'
import Link from 'next/link'

import { Badge } from '@/components/common/BadgeV2'

import type { ParentAward } from '@/types/usaspending'

import { CardV2 } from '../../common/CardV2'

// ============================================================================
// Types
// ============================================================================

interface ParentIdvCardProps {
  data: ParentAward
}

// ============================================================================
// Utility
// ============================================================================

function hasParentIdvData(data: ParentAward | null | undefined): boolean {
  return !!(data?.piid || data?.generatedUniqueAwardId)
}

// ============================================================================
// Component
// ============================================================================

export const ParentIdvCard: React.FC<ParentIdvCardProps> = ({ data }) => {
  if (!hasParentIdvData(data)) return null

  const {
    piid,
    generatedUniqueAwardId,
    idvType,
    idvTypeDescription,
    multipleOrSingleAward,
    multipleOrSingleAwardDescription,
    typeOfIdc,
    typeOfIdcDescription,
    awardingAgency,
  } = data

  // Build display values
  const vehicleType = idvTypeDescription || idvType
  const awardIndicator =
    multipleOrSingleAwardDescription || multipleOrSingleAward
  const idcType = typeOfIdcDescription || typeOfIdc
  const agencyName = awardingAgency?.subtierName || awardingAgency?.toptierName

  return (
    <CardV2 variant="accent">
      <CardV2.Header>
        <CardV2.HeaderIcon>
          <FileText />
        </CardV2.HeaderIcon>
        <CardV2.HeaderTitle>Parent IDV</CardV2.HeaderTitle>
        {vehicleType && (
          <Badge variant="blue" className="text-xs">
            {vehicleType}
          </Badge>
        )}
        {awardIndicator && (
          <Badge variant="outline" className="text-xs">
            {awardIndicator}
          </Badge>
        )}
      </CardV2.Header>
      <CardV2.Content>
        <div className="space-y-2">
          {/* PIID Link */}
          {generatedUniqueAwardId ? (
            <Link
              href={`/awards/usaspending/${encodeURIComponent(generatedUniqueAwardId)}`}
              className="group flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-blue-600 dark:text-accent-400 dark:hover:text-blue-400"
            >
              <span className="font-mono">{piid}</span>
              <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          ) : (
            <span className="font-mono text-sm font-semibold text-primary-900 dark:text-primary-100">
              {piid}
            </span>
          )}

          {/* Details */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-primary-600 dark:text-primary-200">
            {agencyName && <span>{agencyName}</span>}
            {idcType && <span>IDC: {idcType}</span>}
          </div>
        </div>
      </CardV2.Content>
    </CardV2>
  )
}

export { hasParentIdvData }
